/**
 * Custom Element pour la gestion des blocs "prenez rendez-vous" et "être rappelé immédiatement"
 */
class MeetingsAndCallme extends HTMLElement {
  constructor() {
    super();

    // Timeouts pour les validations (éviter les conflits avec frappe rapide)
    this.phoneValidationTimeout = null;

    // Instance intl-tel-input pour gérer les numéros de téléphone internationaux
    this.phoneInputInstance = null;

    // Binding des méthodes pour conserver le contexte
    this.handleClick = this.handleClick.bind(this);

    // Gestionnaire de statut téléphonique (utilise l'utilitaire partagé)
    this.phoneStatusManager = null;
  }

  connectedCallback() {
    this.initializeEventListeners();
    this.initializePhoneInput();
    this.initializePhoneStatus();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    if (this.phoneInputInstance) {
      this.phoneInputInstance.destroy();
    }
    if (this.phoneStatusManager) {
      this.phoneStatusManager.stop();
    }
  }

  /**
   * Initialise le gestionnaire de statut téléphonique via l'utilitaire partagé
   */
  initializePhoneStatus() {
    // Attendre que l'utilitaire soit chargé
    if (typeof window.PhoneStatusUtils === 'undefined') {
      setTimeout(() => this.initializePhoneStatus(), 50);
      return;
    }

    const directCallCTA = this.querySelector('.meetings-and-callme__direct-call-cta');
    if (!directCallCTA) return;

    this.phoneStatusManager = window.PhoneStatusUtils.createStatusManager({
      element: this,
      statusElement: directCallCTA,
      availableClass: 'meetings-and-callme__direct-call-cta--available',
      unavailableClass: 'meetings-and-callme__direct-call-cta--busy',
    });

    this.phoneStatusManager.start();
  }

  initializePhoneInput() {
    const phoneInput = this.querySelector('.meetings-and-callme__callme-input');
    if (!phoneInput) return;

    // Attendre que la bibliothèque soit chargée (defer)
    if (typeof window.intlTelInput === 'undefined') {
      setTimeout(() => this.initializePhoneInput(), 100);
      return;
    }

    try {
      this.phoneInputInstance = window.intlTelInput(phoneInput, {
        containerClass: 'meetings-and-callme__callme-iti hide-on-result',
        initialCountry: 'fr',
        countryOrder: ['fr', 'be', 'ch', 'lu'],
        loadUtils: () => import('https://cdn.jsdelivr.net/npm/intl-tel-input@25.14.0/build/js/utils.js'),
        separateDialCode: true,
        formatOnDisplay: true,
        autoPlaceholder: 'aggressive',
        i18n: {
          searchPlaceholder: 'Recherche',
        },
      });
    } catch (error) {
      // Fallback silencieux si la bibliothèque échoue
    }
  }

  initializeEventListeners() {
    this.addEventListener('click', this.handleClick);

    this.addEventListener('input', this.handleInputValidation.bind(this));
  }

  /**
   * Gestionnaire principal des clics avec event delegation
   * @param {Event} event - L'événement de clic
   */
  handleClick(event) {
    const target = event.target;
    const callmeBtn = target.closest('.meetings-and-callme__callme-cta');

    if (callmeBtn) {
      this.handleCallmeRequest(event);
    }
  }

  /**
   * Gestionnaire de validation en temps réel pour les inputs
   * @param {Event} event - L'événement input
   */
  handleInputValidation(event) {
    const target = event.target;

    // Validation spécifique pour l'input téléphone
    if (target.type === 'tel') {
      // Validation du format téléphone international
      this.validatePhoneNumber(target);
    }
  }

  /**
   * Validation du numéro de téléphone international
   * @param {HTMLInputElement} input - L'input téléphone
   */
  validatePhoneNumber(input) {
    const errorMessage = 'Veuillez saisir un numéro de téléphone valide';
    const errorElement = this.querySelector('.meetings-and-callme__callme-error');

    // Annuler le timeout précédent s'il existe
    if (this.phoneValidationTimeout) {
      clearTimeout(this.phoneValidationTimeout);
    }

    // Supprimer les classes d'erreur existantes
    input.classList.remove('error');
    if (errorElement) {
      errorElement.classList.remove('show');
      errorElement.textContent = '';
    }

    if (!input.value || input.value.length === 0) {
      input.setCustomValidity('');
      return;
    }

    // Si intl-tel-input est initialisé, utiliser sa validation
    if (this.phoneInputInstance) {
      const isValid = this.phoneInputInstance.isValidNumber();

      if (!isValid) {
        input.setCustomValidity(errorMessage);
        input.classList.add('error');

        if (errorElement) {
          // Délai pour éviter l'affichage trop rapide pendant la saisie
          this.phoneValidationTimeout = setTimeout(() => {
            errorElement.textContent = errorMessage;
            errorElement.classList.add('show');
          }, 500);
        }
      } else {
        input.setCustomValidity('');
      }

      this.updateSubmitButtonState(isValid, errorMessage ?? null);
    } else {
      console.warn('[meetings-and-callme]: validation via librairie intl-tel-input indisponible, fallback utilisé');
      // Fallback: validation basique si intl-tel-input n'est pas disponible
      const phoneRegex = /^[\d\s\-\+\(\)]+$/;
      const minLength = 8;

      if (!phoneRegex.test(input.value) || input.value.replace(/\D/g, '').length < minLength) {
        const errorMessage = 'Veuillez saisir un numéro de téléphone valide';
        input.setCustomValidity(errorMessage);
        input.classList.add('error');

        if (errorElement) {
          this.phoneValidationTimeout = setTimeout(() => {
            errorElement.textContent = errorMessage;
            errorElement.classList.add('show');
          }, 500);
        }
      } else {
        input.setCustomValidity('');
      }
    }
  }

  /**
   * Met à jour l'état du bouton de validation selon la validité des champs
   */
  updateSubmitButtonState(isValid, errorMsg = null) {
    const submitBtn = this.querySelector('.meetings-and-callme__cta--callme');

    if (isValid) {
      submitBtn.removeAttribute('disabled');
      submitBtn.removeAttribute('title');
    } else {
      submitBtn.setAttribute('disabled', 'true');
      submitBtn.setAttribute('title', errorMsg ?? 'Erreur de validation');
    }
  }

  handleCallmeRequest(event) {
    event.preventDefault();
    event.stopPropagation();

    const phoneInput = this.querySelector('.meetings-and-callme__callme-input');
    // Dernier check de validation avant envoi
    this.validatePhoneNumber(phoneInput);
    if (phoneInput && phoneInput.checkValidity()) {
      let phoneNumber = phoneInput.value;

      if (this.phoneInputInstance) {
        try {
          phoneNumber = this.phoneInputInstance.getNumber();
        } catch (error) {
          // Fallback sur la valeur brute si erreur
          phoneNumber = phoneInput.value;
        }
      }
      this.submitToAPI({ phone: phoneNumber });
    }
  }

  showResult(
    success = false,
    message = 'Erreur inconnue avec le formulaire de rappel. Merci de réessayer plus tard ou nous appeler au 04 84 98 00 28',
  ) {
    const successElement = this.querySelector('.meetings-and-callme__callme-result');
    const inputGroupElements = this.querySelectorAll('.hide-on-result');

    if (successElement) {
      successElement.classList.add('show');
      successElement.textContent = message;
      inputGroupElements.forEach((element) => {
        element.classList.add('hide');
      });

      if (success) {
        successElement.classList.add('success');
        successElement.classList.remove('error');
      } else {
        successElement.classList.add('error');
        successElement.classList.remove('success');
      }
    }
  }

  /**
   * Soumet les données à l'API N8N
   * @param {Object} data - Les données du formulaire
   */
  async submitToAPI(data) {
    const apiUrl = window.meetingsAndCallmePreprod
      ? 'https://mintbikes.app.n8n.cloud/webhook/preprod-form-rappel-immediat'
      : 'https://mintbikes.app.n8n.cloud/webhook/form-rappel-immediat';
    const payload = {
      phone: data.phone,
    };

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (result) {
        this.showResult(true, 'Merci ! Un conseiller vous rappelle au plus vite');
      } else {
        this.showResult(
          false,
          'Une erreur est survenue. Merci de réessayer plus tard ou nous appeler au 04 84 98 00 28',
        );
      }
    } catch (error) {
      console.error(error);
      this.showResult(
        false,
        "Erreur d'envoi de demande de rappel. Réessayer plus tard ou nous appeler au 04 84 98 00 28",
      );
    }
  }
}

// Enregistrer le Custom Element
customElements.define('meetings-and-callme', MeetingsAndCallme);
